
import {defineComponent} from 'vue';
import {config} from '@/helpers/fetch-config';
import {generateUUIDv4} from "@/helpers/functions/string";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {DocumentRestService} from "@/services/rest/master/DocumentRestService";
import {max} from "@vee-validate/rules";
import BaseDeleteButton from "@/components/UI/Bootstrap/BaseDeleteButton.vue";
import {PropOptions} from "vue-class-component";
import {FormMixin} from "@/components/base/FormMixin";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {UIStateDto} from "@/dtos/UIStateDto";

export default defineComponent({
  name: 'InputImage',
  mixins: [FormMixin],
  components: {AlertError2, BaseDeleteButton},
  emits: ["imageCleared", "update:modelValue", "previewUrlChanged"],
  props: {
    modelValue: {
      type: String,
      required: false,
    } as PropOptions<string | null>,
    id: {
      type: String,
      required: false,
      default: generateUUIDv4('file-input-'),
    },
    module: {
      type: String,
      required: false,
      default: DpModuleEnum.CMS,
    },
    maxHeight: {
      type: String,
      required: false,
      default: '200px',
    },
    label: {
      type: String,
      default: '',
    },
    typeCode: {
      type: String,
      required: true,
    },
    useSignedUrl: {
      type: Boolean,
      required: false,
      default: false
    },
    canBeDeleted: {
      type: Boolean,
      required: false,
      default: true,
    },
    maxFileSize: {
      type: Number,
      required: false,
      default: config.uploadFileSizeLimitMB ?? 10
    },
    showFullSizePreviewOnHover: {
      type: Boolean,
      required: false,
      default: false
    },
    rules: {
      type: String,
      required: false,
      default: '',
    },
    isMultilingual: {
      type: Boolean,
      required: false,
      default: false,
    },
    language: {
      type: String,
      required: false,
      default: 'nl'
    }
  },
  data: () => ({
    file: null as File | null,
    previewUrl: '',
    documentKey: null as string | null,
    isHovering: false as boolean,
  }),
  watch: {
    modelValue: {
      handler() {
        this.reloadContent();
      },
      immediate: true
    }
  },
  computed: {
    fileName(): string {
      return this.previewUrl.substr(this.previewUrl.lastIndexOf('/') + 1);
    },
    isRequired(): boolean {
      return this.rules !== undefined && this.rules.includes("required");
    },
    isDocument(): boolean {
      return this.typeCode === 'PAGE_DOC' || this.typeCode === 'REIMBURSED_CARE_RATES_DOC';
    }
  },
  methods: {
    max,
    async doReload(): Promise<void> {
      if (this.modelValue) {
        await this.searchPreviewDocument(this.modelValue);
      } else {
        this.previewUrl = '';
      }
    },
    dragover(event: any) {
      if (!event.currentTarget.classList.contains('dragging')) {
        event.currentTarget.classList.add('dragging');
      }
    },
    dragleave(event: any) {
      event.currentTarget.classList.remove('dragging');
    },
    drop(event: any) {
      event.currentTarget.classList.remove('dragging');
    },
    setIsHovering(hoverState: boolean) {
      this.isHovering = hoverState;
    },
    async deleteDocument(): Promise<void> {
      await this.performGenericDelete(this.doDelete, this.doNothing, this.pageUI as UIStateDto, false);
    },
    async doDelete(): Promise<void> {
      // const uploadInput: any = this.$refs[`fileupload-${this.id}`];
      // uploadInput.value = null;
      this.file = null;
      this.previewUrl = '';
      this.documentKey = null;
      this.$emit('update:modelValue', null);
      this.$emit('previewUrlChanged', null);
      this.$emit('imageCleared');
    },
    async handleChosenFile(event: any): Promise<void> {
      this.file = event.target.files[0];

      if (!this.file) {
        return;
      }

      if (this.file.size > (this.maxFileSize ?? 10) * 1024 * 1024) {
        event.target.value = '';
        throw new Error(this.$t('uploadFileSizeTooBig', {maxSize: this.maxFileSize}));
      }
      await this.performGenericSubmit(this.doUpload, this.afterUpload);
    },
    async searchPreviewDocument(documentKey: String) {
      if (this.useSignedUrl) {
        this.previewUrl = await DocumentRestService.getInstance().findSignedPreviewUrl(documentKey, this.module);
      } else {
        this.previewUrl = await DocumentRestService.getInstance().findPublicPreviewUrl(documentKey, this.module);
      }
    },
    async doUpload(): Promise<void> {
      this.documentKey = await DocumentRestService.getInstance().uploadFile(this.typeCode, this.file, this.module);
    },
    async afterUpload(): Promise<void>{
      if (this.documentKey) {
        await this.searchPreviewDocument(this.documentKey);
        this.$emit('update:modelValue', this.documentKey);
        this.$emit('previewUrlChanged', this.previewUrl);
      } else {
        throw new Error('No document key');
      }
    },
  },
});
